import useAnalyticsCalls from '@/hooks/use-analytics';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { Box, Flex, IconInfoCircle, Image, Text, useBreakpointValue } from '@cmpsr/components';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { text } from '../../../common/theme';
import { ComparisonModalContent } from './ComparisonModalContent';
import { VARS } from '../../../common/constants';
import MarkdownText from '@/components/markdown/markdown-text';

// Type for responsive width
export type ResponsiveWidth = { md: string; base: string };

// Type for column configuration
export interface ColumnConfig {
  width: string | ResponsiveWidth;
  fillColor: string;
  textColor: string;
  textAlign?: 'left' | 'center' | 'right';
  borderColorLeft?: string;
  borderColorRight?: string;
  borderColorTop?: string;
  borderColorBottom?: string;
}

// Type for cell-specific configuration
export interface CellConfig {
  fillColor?: string;
  textColor?: string;
  borderColorLeft?: string;
  borderColorRight?: string;
  borderColorTop?: string;
  borderColorBottom?: string;
  minHeight?: string;
  fontSize?: string | { [key: string]: string };
}

// Cell component props
export interface CellProps {
  children: React.ReactNode;
  isHidden?: boolean;
  topLeftRadius?: boolean;
  topRightRadius?: boolean;
  bottomLeftRadius?: boolean;
  bottomRightRadius?: boolean;
  fillColor?: string;
  textColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  isLastColumn?: boolean;
  isLabel?: boolean;
  needsBottomBorder?: boolean;
  width?: string | ResponsiveWidth;
  borderColorLeft?: string;
  borderColorRight?: string;
  borderColorTop?: string;
  borderColorBottom?: string;
  minHeight?: string;
  fontSize?: string | { [key: string]: string };
}

// Animation options
export interface AnimationOptions {
  enabled: boolean;
  totalDuration: number; // in milliseconds
}

// Type for our special markers
interface CheckmarkCellMarker {
  __type: 'checkmark';
}

// ComparisonTable props
export interface ComparisonTableProps {
  tableMatrix?: React.ReactNode[][];
  columnConfig?: ColumnConfig[];
  cellSpecificConfig?: Record<string, CellConfig>;
  backgroundColor?: string;
  defaultTextColor?: string;
  animation?: AnimationOptions;
  /**
   * Optional: Map of row indices to modal content. If a row index is present, that row is clickable and opens the modal with the given content.
   */
  rowModalConfig?: RowModalConfig;
}

// Marker for checkmark cells (now properly typed)
const CHECKMARK_CELL: CheckmarkCellMarker & React.ReactNode = { __type: 'checkmark' } as any;

// Reusable InfoCellLabel component
const InfoCellLabel = ({ children }: { children: React.ReactNode }) => (
  <Box position="relative" width="100%">
    <Flex alignItems="center" gap={1}>
      {children}
      <Box as="sup" sx={{ lineHeight: 1, ml: '2px', verticalAlign: 'super' }} display={{ base: 'none', md: 'inline' }}>
        <IconInfoCircle size="xs" />
      </Box>
    </Flex>
    <Box
      as="sup"
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        lineHeight: 1,
        ml: '2px',
        verticalAlign: 'super',
      }}
      display={{ base: 'block', md: 'none' }}
    >
      <IconInfoCircle size="xs" />
    </Box>
  </Box>
);

// Default data for free assessment comparison
const defaultTableMatrix = [
  [null, 'Catch Membership', 'Standard Healthcare'],
  [
    <InfoCellLabel>Personalized Risk Assessment</InfoCellLabel>,
    CHECKMARK_CELL,
    <Text color="salmon-light">$1,500+</Text>,
  ],
  [<InfoCellLabel>Lifetime Risk Scores</InfoCellLabel>, CHECKMARK_CELL, <Text color="salmon-light">$1,000+</Text>],
  [
    <InfoCellLabel>Personalized Testing Protocol</InfoCellLabel>,
    CHECKMARK_CELL,
    <Text color="salmon-light">$1,000+</Text>,
  ],
  [
    <InfoCellLabel>Risk Reduction Action Plan</InfoCellLabel>,
    CHECKMARK_CELL,
    <Text color="salmon-light">$1,500+</Text>,
  ],
  [
    null,
    <MarkdownText color="green" {...text.body.bold}>
      {VARS.price.yearlyDisplay}
    </MarkdownText>,
    <Text color="salmon-light" {...text.body.bold} fontSize="14px">
      $5,000+
    </Text>,
  ],
];

// Default column configuration with placeholder for defaultTextColor and backgroundColor
const getDefaultColumnConfig = (backgroundColor: string, defaultTextColor: string): ColumnConfig[] => [
  { width: 'auto', fillColor: 'darkBlue', textColor: defaultTextColor, textAlign: 'left' }, // First column
  {
    width: { md: '130px', base: '94px' },
    fillColor: 'gradient.dark',
    textColor: defaultTextColor,
    textAlign: 'center',
    borderColorLeft: '#7CF1C7',
    borderColorTop: 'transparent',
    borderColorBottom: 'transparent',
  }, // Second column
  {
    width: { md: '130px', base: '94px' },
    fillColor: 'darkBlue',
    textColor: 'white',
    textAlign: 'center',
    borderColorLeft: '#7CF1C7',
  }, // Third column
];

// Default cell-specific configuration
const defaultCellSpecificConfig: Record<string, CellConfig> = {
  // Format: "rowIndex,colIndex": { fillColor, textColor }
  '5,1': { borderColorTop: '#7CF1C7', minHeight: '32px', fontSize: '14px' }, // "Complete Coverage" cell
  '5,2': { fontSize: '14px' }, // "Complete Coverage" cell
};

// Extracted Cell component and memoized
const Cell = memo(
  ({
    children,
    topLeftRadius = false,
    topRightRadius = false,
    bottomLeftRadius = false,
    bottomRightRadius = false,
    fillColor = 'tan',
    textColor = 'darkBlue',
    textAlign = 'center',
    isLabel = false,
    isLastColumn = false,
    needsBottomBorder = false,
    width,
    borderColorLeft,
    borderColorRight,
    borderColorTop,
    borderColorBottom,
    minHeight,
    fontSize,
  }: CellProps) => {
    // Always call hooks at the top level
    const checkmarkImageSrc = useBreakpointValue({
      base: '/images/green-check-egg-clear.svg',
      md: '/images/green-check-egg-horizontal-clear.svg',
    });

    // For empty cells (null), render with no borders and transparent background
    if (children === null) {
      return (
        <Box
          as="td"
          p="spacer-4"
          bg="transparent"
          sx={{
            border: 'none',
            width,
          }}
        />
      );
    }

    // Handle checkmark cells - updated type check
    if (children && typeof children === 'object' && '__type' in children && children.__type === 'checkmark') {
      // Replace children with the appropriate Image component
      children = <Image src={checkmarkImageSrc} alt="Check mark" width={22} height={22} />;
    }

    // Determine if this is a header cell based on row position
    const isHeader = typeof children === 'string' && (children === 'Without Catch' || children === 'With Catch');

    // For visible cells
    const borderRadius = {
      ...(topLeftRadius ? { borderTopLeftRadius: '10px' } : {}),
      ...(topRightRadius ? { borderTopRightRadius: '10px' } : {}),
      ...(bottomLeftRadius ? { borderBottomLeftRadius: '10px' } : {}),
      ...(bottomRightRadius ? { borderBottomRightRadius: '10px' } : {}),
    };

    // Default border color
    const defaultBorderColor = '#38749D';

    // Apply borders selectively to avoid double borders
    const borderStyle = {
      borderTop: `1px solid ${borderColorTop || defaultBorderColor}`,
      borderRight: isLastColumn ? `1px solid ${borderColorRight || defaultBorderColor}` : 'none',
      borderBottom: needsBottomBorder ? `1px solid ${borderColorBottom || defaultBorderColor}` : 'none',
      borderLeft: `1px solid ${borderColorLeft || defaultBorderColor}`,
    };

    return (
      <Box
        as={isHeader ? 'th' : 'td'}
        p="spacer-2"
        textAlign={textAlign}
        fontFamily={isLabel ? 'inter' : 'libreBaskerville'}
        fontStyle={isLabel ? 'normal' : 'italic'}
        fontWeight={400}
        fontSize={fontSize !== undefined ? fontSize : { base: '12px', md: '14px' }}
        color={textColor}
        bg={fillColor}
        sx={{
          ...borderRadius,
          ...borderStyle,
          overflow: 'hidden',
          width,
        }}
      >
        {/* Flex centering for images and content if textAlign is center */}
        {textAlign === 'center' ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            sx={minHeight ? { minHeight } : {}}
          >
            {children || ''}
          </Box>
        ) : (
          <Box sx={minHeight ? { minHeight } : {}}>{children || ''}</Box>
        )}
      </Box>
    );
  }
);

Cell.displayName = 'Cell';

// TableRow component
interface TableRowProps {
  row: React.ReactNode[];
  rowIndex: number;
  tableMatrix: React.ReactNode[][];
  getCellProps: (cell: React.ReactNode, rowIndex: number, colIndex: number, matrix: React.ReactNode[][]) => CellProps;
  backgroundColor: string;
  animationDelay?: number; // Delay in ms for animation
  animationEnabled?: boolean; // Whether animation is enabled
  onClickRow?: (rowIndex: number) => void;
  isClickable?: boolean;
}

const TableRow = memo(
  ({
    row,
    rowIndex,
    tableMatrix,
    getCellProps,
    backgroundColor,
    animationDelay = 0,
    animationEnabled = false,
    onClickRow,
    isClickable = false,
  }: TableRowProps) => {
    return (
      <Box
        as="tr"
        bg={backgroundColor}
        sx={{
          opacity: animationEnabled ? 0 : 1,
          animation: animationEnabled ? `fadeIn 0.5s ease-in-out forwards ${animationDelay}ms` : 'none',
          '@keyframes fadeIn': {
            '0%': { opacity: 0, transform: 'translateY(10px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
          cursor: isClickable ? 'pointer' : 'default',
          transition: isClickable ? 'background 0.2s' : undefined,
          '&:hover': isClickable ? { background: 'rgba(60, 180, 200, 0.08)' } : undefined,
        }}
        onClick={isClickable && onClickRow ? () => onClickRow(rowIndex) : undefined}
        tabIndex={isClickable ? 0 : undefined}
        role={isClickable ? 'button' : undefined}
        aria-pressed={isClickable ? 'false' : undefined}
      >
        {row.map((cell, colIndex) => {
          const cellProps = getCellProps(cell, rowIndex, colIndex, tableMatrix);
          return <Cell key={colIndex} {...cellProps} />;
        })}
      </Box>
    );
  }
);

TableRow.displayName = 'TableRow';

interface RowModalConfig {
  [rowIndex: number]: React.ReactNode;
}

// Define slugs for each row type
const COMPARISON_ITEM_SLUGS: Record<number, string> = {
  1: 'personalized-risk-assessment',
  2: 'lifetime-risk-scores',
  3: 'personalized-testing-protocol',
  4: 'risk-reduction-action-plan',
};

// Define titles for each row type
const COMPARISON_ITEM_TITLES: Record<number, string> = {
  1: 'Personalized Risk Assessment',
  2: 'Lifetime Risk Scores',
  3: 'Personalized Testing Protocol',
  4: 'Risk Reduction Action Plan',
};

const getDefaultRowModalConfig = (
  onClose: () => void,
  handleModalView: (rowIndex: number) => void
): RowModalConfig => ({
  1: (
    <ComparisonModalContent
      onClose={onClose}
      includedContent="**Included with Catch:**  \nA Personalized Risk Assessment analyzing 500+ cancer risk factors is included in your membership."
      traditionalContent="**Traditional Healthcare:**  \nIn standard healthcare, genetic screening is the most common risk analysis, with a basic genetic screening often costing **over $1,500** and typically covers just a handful of genes like BRCA1 or APC. It doesn’t include lifestyle, environmental, or family history integration — and usually requires a referral to a specialist."
    />
  ),
  2: (
    <ComparisonModalContent
      onClose={onClose}
      includedContent="**Included with Catch:**  \nLifetime Risk Scores for the top 21 major cancers is included in your membership."
      traditionalContent="To get risk scores for 21 major cancers, you'd need multiple specialist visits and separate tests, which can quickly **exceed $2,000**. Traditional systems don't provide a unified lifetime risk score like Catch does."
    />
  ),
  3: (
    <ComparisonModalContent
      onClose={onClose}
      includedContent="**Included with Catch:**  \nAn Annual Testing Protocol for early cancer detection based on your unique risk profile is included in your catch membership."
      traditionalContent="**Traditional Healthcare:**  \nA tailored testing and screening plan based on your personal risk is rarely available in standard care. It typically requires consultations with specialists and out-of-pocket costs **upward of $1,000** — and even then, it’s usually based on general population guidelines, not your specific profile."
    />
  ),
  4: (
    <ComparisonModalContent
      onClose={onClose}
      includedContent="**Included with Catch:**  \nA Comprehensive Action Plan to reduce your lifetime risk is included in your membership."
      traditionalContent="**Traditional Healthcare:**  \nAn action plan to mitigate the diversity of your individual risk factors would require consultations with specialists across many disciplines and cost **upward of $1,500** with high complexity to manage across different specialty services."
    />
  ),
});

const ComparisonTable = ({
  tableMatrix = defaultTableMatrix,
  columnConfig,
  cellSpecificConfig = defaultCellSpecificConfig,
  backgroundColor = 'tan',
  defaultTextColor = 'darkBlue',
  animation,
  rowModalConfig,
}: ComparisonTableProps) => {
  // Use the provided columnConfig or generate the default one with the correct backgroundColor and defaultTextColor
  const effectiveColumnConfig = columnConfig || getDefaultColumnConfig(backgroundColor, defaultTextColor);

  // Animation settings
  const animationEnabled = animation?.enabled || false;
  const totalDuration = animation?.totalDuration || 1000; // Default to 1 second if not specified

  // Analytics hook
  const analytics = useAnalyticsCalls();

  const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);
  const handleCloseModal = useCallback(() => setOpenRowIndex(null), []);

  // Track modal view analytics
  const handleModalView = useCallback(
    (rowIndex: number) => {
      const slug = COMPARISON_ITEM_SLUGS[rowIndex];
      const title = COMPARISON_ITEM_TITLES[rowIndex];

      if (slug && title) {
        analytics.callModalViewed('comparison', {
          item_title: title,
          item_slug: slug
        });
      }
    },
    [analytics]
  );

  const effectiveRowModalConfig = rowModalConfig || getDefaultRowModalConfig(handleCloseModal, handleModalView);

  const handleRowClick = useCallback(
    (rowIndex: number) => {
      if (effectiveRowModalConfig && effectiveRowModalConfig[rowIndex]) {
        setOpenRowIndex(rowIndex);

        // Track modal view when opening
        const slug = COMPARISON_ITEM_SLUGS[rowIndex];
        const title = COMPARISON_ITEM_TITLES[rowIndex];

        if (slug && title) {
          analytics.callModalViewed('comparison', {
            item_title: title,
            item_slug: slug
          });
        }
      }
    },
    [effectiveRowModalConfig, analytics]
  );

  // Function to determine cell properties based on position and content - memoized
  const getCellProps = useMemo(() => {
    return (cell: React.ReactNode, rowIndex: number, colIndex: number, matrix: React.ReactNode[][]): CellProps => {
      const isHeaderRow = rowIndex === 0;
      const isLastRow = rowIndex === matrix.length - 1;
      const isLastColumn = colIndex === matrix[rowIndex].length - 1;
      const isFirstColumn = colIndex === 0;
      const isEmpty = cell === null;

      // Get column configuration
      const colConfig = effectiveColumnConfig[colIndex] || {
        fillColor: backgroundColor,
        textColor: defaultTextColor,
        width: 'auto',
        textAlign: 'center', // default fallback
      };

      // Check for cell-specific configuration
      const cellKey = `${rowIndex},${colIndex}`;
      const cellConfig = cellSpecificConfig[cellKey] || {};

      // Merge configurations, with cell-specific taking precedence
      const fillColor = cellConfig.fillColor || colConfig.fillColor;
      const textColor = cellConfig.textColor || colConfig.textColor;
      const borderColorLeft = cellConfig.borderColorLeft || colConfig.borderColorLeft;
      const borderColorRight = cellConfig.borderColorRight || colConfig.borderColorRight;
      const borderColorTop = cellConfig.borderColorTop || colConfig.borderColorTop;
      const borderColorBottom = cellConfig.borderColorBottom || colConfig.borderColorBottom;
      const minHeight = cellConfig.minHeight;
      const fontSize = cellConfig.fontSize;

      // Check if the cell below is empty (null)
      const cellBelowIsEmpty = isLastRow || (rowIndex < matrix.length - 1 && matrix[rowIndex + 1][colIndex] === null);
      const cellLeftIsEmpty = isFirstColumn || matrix[rowIndex][colIndex - 1] === null;
      const needsBottomBorder = isLastRow || cellBelowIsEmpty;

      // Determine textAlign: use colConfig.textAlign unless it's the first column and not a header row
      const textAlign = colConfig.textAlign || (colIndex === 0 && !isHeaderRow ? 'left' : 'center');

      // For empty cells (null), don't apply borders
      if (isEmpty) {
        return {
          children: null,
          fillColor,
          width: colConfig.width,
          textAlign,
        };
      }

      // For all other cells
      return {
        children: cell,
        isLastColumn,
        needsBottomBorder,
        isLabel: colIndex === 0 || isHeaderRow,
        textAlign,
        topLeftRadius: (isHeaderRow && colIndex === 1) || (rowIndex === 1 && colIndex === 0),
        topRightRadius: isHeaderRow && isLastColumn,
        bottomLeftRadius: cellLeftIsEmpty && cellBelowIsEmpty,
        bottomRightRadius: isLastRow && isLastColumn,
        fillColor,
        textColor,
        width: colConfig.width,
        borderColorLeft,
        borderColorRight,
        borderColorTop,
        borderColorBottom,
        minHeight,
        fontSize,
      };
    };
  }, [effectiveColumnConfig, cellSpecificConfig, backgroundColor, defaultTextColor]);

  // Calculate the number of non-empty rows for animation timing
  const nonEmptyRowCount = useMemo(() => {
    return tableMatrix.filter((row) => row.some((cell) => cell !== null)).length;
  }, [tableMatrix]);

  // Memoize the table rows to prevent unnecessary re-renders
  const memoizedRows = useMemo(() => {
    // Calculate the delay per row based on total duration and number of rows
    const delayPerRow = nonEmptyRowCount > 0 ? totalDuration / nonEmptyRowCount : 0;

    return tableMatrix.map((row, rowIndex) => {
      // Skip animation for completely empty rows
      const hasContent = row.some((cell) => cell !== null);
      const animationDelay = hasContent ? rowIndex * delayPerRow : 0;
      const isClickable = !!effectiveRowModalConfig && !!effectiveRowModalConfig[rowIndex];
      return (
        <TableRow
          key={rowIndex}
          row={row}
          rowIndex={rowIndex}
          tableMatrix={tableMatrix}
          getCellProps={getCellProps}
          backgroundColor={backgroundColor}
          animationDelay={animationDelay}
          animationEnabled={animationEnabled && hasContent}
          onClickRow={handleRowClick}
          isClickable={isClickable}
        />
      );
    });
  }, [
    tableMatrix,
    getCellProps,
    backgroundColor,
    animationEnabled,
    totalDuration,
    nonEmptyRowCount,
    effectiveRowModalConfig,
    handleRowClick,
  ]);

  return (
    <Box width="100%">
      <Box width="100%" borderRadius="10px" overflow="hidden">
        <Box
          as="table"
          width="100%"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed', // This ensures the table respects the column widths
          }}
          bg={backgroundColor}
        >
          <Box as="tbody">{memoizedRows}</Box>
        </Box>
      </Box>
      {openRowIndex !== null && effectiveRowModalConfig[openRowIndex] && (
        <Modal isOpen={true} onClose={handleCloseModal} size="auto">
          <ModalOverlay backdropFilter="blur(2px)" bg="rgba(24, 51, 68, 0.60)" />
          <ModalContent>
            <ModalBody bg="tan">{effectiveRowModalConfig[openRowIndex]}</ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default ComparisonTable;
